import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';
import UploadImages from '../components/UploadImages';
import VariationsComponent from '../components/VariationsComponent';
import Pricing from '../components/Pricing';
import CategoryProduct from '../components/CategoryProduct';
import InputUi from '../components/InputUi';
import RichTextUi from '../components/RichTextUi';
import { listSizeCharts } from '../lib/api/sizeChartApi';


// Container Component
const Container = ({ label, children, gap = 3, containerclass = '' }) => (
    <div className={`card p-6 ${containerclass}`}>
        {label && <h3 className="text-lg font-semibold mb-4 text-[var(--text)]">{label}</h3>}
        <div className={`flex flex-col gap-${gap}`}>
            {children}
        </div>
    </div>
);



const AddProduct = () => {
    const navigate = useNavigate();
    const uploadRef = useRef();
    const galleryRef = useRef();

    const [product, setProduct] = useState({ type: 'variable', brand: 'BRANDNAME' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sizeCharts, setSizeCharts] = useState([]);

    // Fetch size charts
    React.useEffect(() => {
        const fetchSizeCharts = async () => {
            try {
                const res = await listSizeCharts();
                if (res.success) {
                    setSizeCharts(res.data || []);
                }
            } catch (err) {
                console.error('Error fetching size charts:', err);
            }
        };
        fetchSizeCharts();
    }, []);

    const updateFunction = (data, name) => {
        setProduct(prev => ({
            ...prev,
            [name]: data.target.value
        }));
        console.log(product);
    };

    const handleUpload = async () => {
        try {
            setLoading(true);
            setError('');
            const totalStart = performance.now();

            // Step 1: Upload images in parallel
            console.log('[AddProduct] ⏳ Step 1/2: Uploading featured + gallery images in parallel...');
            const imgStart = performance.now();
            const [finalImages, galleryupload] = await Promise.all([
                uploadRef.current?.uploadImageFunction() ?? Promise.resolve([]),
                galleryRef.current?.uploadImageFunction() ?? Promise.resolve([])
            ]);
            const imgDuration = ((performance.now() - imgStart) / 1000).toFixed(2);
            console.log(`[AddProduct] ✅ Images done in ${imgDuration}s — Featured: ${finalImages?.length || 0}, Gallery: ${galleryupload?.length || 0}`);

            const fullProductData = {
                ...product,
                featuredImage: finalImages,
                galleryImage: galleryupload
            };

            // Step 2: POST to backend
            console.log('[AddProduct] ⏳ Step 2/2: Sending product data to server...');
            const apiStart = performance.now();
            const { data: result } = await axiosInstance.post(
                '/brand/add-product',
                fullProductData
            );
            const apiDuration = ((performance.now() - apiStart) / 1000).toFixed(2);
            console.log(`[AddProduct] ✅ Server responded in ${apiDuration}s:`, result);

            const totalDuration = ((performance.now() - totalStart) / 1000).toFixed(2);
            console.log(`[AddProduct] 🎉 Product uploaded successfully! Total time: ${totalDuration}s`);
            alert('Product Added');
            navigate('/products');
        } catch (error) {
            console.error('[AddProduct] ❌ Upload failed:', error.response?.data || error.message);
            setError(error.response?.data?.message || 'Failed to add product');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/products')}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            ← Back to Products
                        </button>
                        <h1 className="lux-heading text-2xl">Add Product</h1>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-6 gap-2">
                    <div className="col-span-4 gap-2">
                        <div className="flex flex-col gap-2">
                            <Container gap={3} label={'Basic Information'}>
                                <InputUi label={'Product Title'} datafunction={(e) => updateFunction(e, 'name')} />
                                <RichTextUi label={'Product Description'} value={product.description || ''} onChange={(val) => updateFunction({ target: { value: val } }, 'description')} />
                                <div className="grid grid-cols-3 gap-2">
                                    <RichTextUi label={'Tab 1 - Material Care'} value={product.tab1 || ''} onChange={(val) => updateFunction({ target: { value: val } }, 'tab1')} />
                                    <RichTextUi label={'Tab 2 - Styling and Suggestion'} value={product.tab2 || ''} onChange={(val) => updateFunction({ target: { value: val } }, 'tab2')} />
                                    <RichTextUi label={'Tab 3 - Product Specifications'} value={product.tab3 || ''} onChange={(val) => updateFunction({ target: { value: val } }, 'tab3')} />
                                </div>
                            </Container>
                            <Container gap={3} label={'Pricing & Discount'}>
                                <Pricing setProducts={setProduct} products={product} />
                            </Container>
                            <Container gap={3} label={'Variation & Stocking'}>
                                <VariationsComponent setProducts={setProduct} products={product} />
                            </Container>

                            <Container gap={3} label={'Size Chart (Optional)'}>
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-medium text-[var(--muted)]">Select Size Chart</label>
                                    <select
                                        className="w-full px-3 py-2 border border-[var(--border)] rounded-md bg-[var(--surface)] text-[var(--text)] text-sm"
                                        value={product.sizeChartUrl || ''}
                                        onChange={(e) => setProduct(prev => ({ ...prev, sizeChartUrl: e.target.value || null }))}
                                    >
                                        <option value="">None</option>
                                        {sizeCharts.map(chart => (
                                            <option key={chart.id} value={chart.imgUrl}>
                                                {chart.chartName}
                                            </option>
                                        ))}
                                    </select>
                                    {product.sizeChartUrl && (
                                        <div className="mt-2">
                                            <p className="text-xs text-[var(--muted)] mb-1">Preview:</p>
                                            <div className="w-40 h-40 border border-[var(--border)] rounded overflow-hidden bg-[var(--bg)]">
                                                <img
                                                    src={product.sizeChartUrl}
                                                    alt="Size chart preview"
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Container>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <div className="flex flex-col gap-2">
                            {/* <Container containerclass={'bg-[var(--surface)]'}>
                                <div className="overflow-x-auto">
                                    <pre className="col-span-2 mt-4 p-2 text-[var(--text)] rounded text-xs whitespace-pre max-w-full">
                                        {JSON.stringify(product, null, 2)}
                                    </pre>
                                </div>
                            </Container> */}
                            <Container label={'Categories'}>
                                <CategoryProduct setProducts={setProduct} products={product} />
                            </Container>
                            <Container gap={3} label={'Featured Images'}>
                                <UploadImages ref={uploadRef} maxImages={2} setProducts={setProduct} products={product} />
                            </Container>
                            <Container gap={3} label={'Gallery Images'}>
                                <UploadImages ref={galleryRef} maxImages={8} setProducts={setProduct} products={product} />
                            </Container>
                            <button
                                className='w-full px-4 py-3 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-lg hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2'
                                onClick={handleUpload}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-[var(--accent-contrast)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                        Uploading...
                                    </>
                                ) : (
                                    'Upload Product'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProduct;
